import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import mapValues from 'lodash/mapValues';
import some from 'lodash/some';
import { HotKeys } from 'react-hotkeys';
import { NavListItem } from '@eventbrite/legacy-eds.nav-list-item';
import { Icon } from '@eventbrite/legacy-eds.icon';
import { ITEMS_PROP_TYPE } from './constants/types';
import { injectIntl } from 'react-intl';

import {
    TABBABLE_INDEX,
    UNTABBABLE_INDEX,
    MOVE_TO_NEXT,
    MOVE_TO_PREV,
    MOVE_TO_FIRST,
    MOVE_TO_LAST,
    ACTIVATE_ITEM,
} from '@eventbrite/legacy-eds.hot-keys';
import { focusNewPosition, filterNonEmptyItems } from '@eventbrite/legacy-eds.hot-keys';
import { ACTION_KEY_MAP } from './hotKeys';

import styles from './NavList.module.scss';

import { ChevronUpChunky } from '@eventbrite/legacy-eds.iconography';
import { ChevronDownChunky } from '@eventbrite/legacy-eds.iconography';

const _getSymbolType = (isExpanded) => (isExpanded ? <ChevronUpChunky /> : <ChevronDownChunky />);
const _getSymbolColor = (isDisabled) => (isDisabled ? 'grey-300' : 'grey-400');
const _isItemExpanded = ({ active, items }) => !!active || some(items, _isItemExpanded);
const _getExpandedItemsLookup = (items) => mapValues(items, (item) => _isItemExpanded(item));

class NavListComponent extends React.PureComponent {
    static propTypes = {
        /**
         * Array of list items. Available types are ANCHOR_LINK, ROUTER_LINK, DIVIDER
         */
        items: ITEMS_PROP_TYPE.isRequired,
        /**
         * Display the list inline (Horizontally aligned)
         */
        displayInline: PropTypes.bool,
        /**
         * Whether or not the list is active
         */
        active: PropTypes.bool,
        /**
         * Use alternate theme navList
         */
        useAltTheme: PropTypes.bool,
        /**
         * Shows a symbol / icon next to the menu items with sub items
         */
        showSymbols: PropTypes.bool,
        /**
         * Default position (zero-based) within item list - received from DropdownMenu
         */
        defaultFocusPosition: PropTypes.number,
        /**
         * Whether the navigation for items should be un-wrapped (i.e. no first to last, last to first)
         */
        preventFocusWrap: PropTypes.bool,
        /**
         * Hide dropdown
         */
        hideDropdown: PropTypes.func,
        /**
         * Use secondary alternate theme navList
         */
        useSecondaryTheme: PropTypes.bool,
        /**
         * Render as nested sub-list
         */
        isSubList: PropTypes.bool,
    };

    static defaultProps = {
        displayInline: false,
        useAltTheme: false,
        showSymbols: false,
        preventFocusWrap: false,
        useSecondaryTheme: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            expandedItemsLookup: _getExpandedItemsLookup(props.items),
            focusPosition: props.defaultFocusPosition,
        };
    }

    UNSAFE_componentWillReceiveProps({ items }) {
        this.setState({ expandedItemsLookup: _getExpandedItemsLookup(items) });
    }

    hotKeyHandlers = {
        [MOVE_TO_NEXT]: (e) => this._changeFocusedNavListItem(e, MOVE_TO_NEXT),
        [MOVE_TO_PREV]: (e) => this._changeFocusedNavListItem(e, MOVE_TO_PREV),
        [MOVE_TO_FIRST]: (e) => this._changeFocusedNavListItem(e, MOVE_TO_FIRST),
        [MOVE_TO_LAST]: (e) => this._changeFocusedNavListItem(e, MOVE_TO_LAST),
        [ACTIVATE_ITEM]: (e) => e.stopPropagation(),
    };

    _changeFocusedNavListItem(e, direction) {
        const { items, preventFocusWrap } = this.props;
        const { focusPosition } = this.state;
        const nonEmptyItems = filterNonEmptyItems(items);
        const boundFocusNewPosition = focusNewPosition.bind(
            null,
            nonEmptyItems,
            focusPosition,
            e,
            preventFocusWrap,
        );

        this.setState(boundFocusNewPosition(direction));
    }

    _handleLinkClick(index, onActivate) {
        const { expandedItemsLookup } = this.state;

        this.setState({
            expandedItemsLookup: {
                ...expandedItemsLookup,
                [index]: !expandedItemsLookup[index],
            },
        });

        if (onActivate) {
            onActivate();
        }
    }

    // NOTE: Merging keyboard event with mouse events so that in the case of a mouseOver,
    // the focusPosition is changed from the one determined from keyboard navigation
    // to the one determined by the mouseOver. This removes multiple highlights within
    // the same list. We use position rather than index because NavListItem's can be
    // dividers and we don't want to set  focus on those.
    _handleItemTentativeSelect(focusPosition) {
        // NOTE: adding this condition to avoid recurrent calls, freezing browser tab
        // this is a quick fix until we find what is causing that behaviour
        // JIRA: https://jira.evbhome.com/browse/EB-150617
        if (this.state.focusPosition !== focusPosition) {
            this.setState({ focusPosition });
        }
    }

    _getNavListItems(items, showSymbols, useAltTheme, defaultFocusPosition) {
        const { expandedItemsLookup, focusPosition } = this.state;
        const { hideDropdown, useSecondaryTheme, isSubList } = this.props;
        let position = -1;

        return items.map(
            (
                {
                    active,
                    content,
                    isDisabled,
                    isNavLinkTextEmphasized = true,
                    items: nestedItems,
                    onActivate,
                    onClick: onItemClick,
                    path,
                    secondaryContent,
                    type,
                    ...unknownItemProps
                },
                index,
            ) => {
                let onTentativeSelect;
                const hasNestedItems = !isEmpty(nestedItems);
                const isExpanded = expandedItemsLookup[index];
                let isTentativelySelected;
                let nestedListComponent;
                let symbolComponent;
                let onLinkClick;
                let tabIndex;
                let secondaryContentComponent;

                const onClick = (event) => {
                    if (onItemClick) onItemClick(event, hideDropdown);
                };

                // For anchor-link NavListItems, which are used in dropdownMenu,
                // (vs router-link NavListItems), assign incremental position. This excludes
                // any dividers that are used, as we don't want to set tabIndex
                // or focus on those.
                if (type === 'anchor' && defaultFocusPosition !== undefined) {
                    position++;
                    isTentativelySelected = position === focusPosition;
                    tabIndex = isTentativelySelected ? TABBABLE_INDEX : UNTABBABLE_INDEX;
                    onTentativeSelect = this._handleItemTentativeSelect.bind(this, position);
                }

                if (hasNestedItems) {
                    onLinkClick = this._handleLinkClick.bind(this, index, onActivate);

                    if (isExpanded) {
                        nestedListComponent = (
                            <div className={styles.subList}>
                                <NavList
                                    data-testid="eds-subnav-list"
                                    items={nestedItems}
                                    useAltTheme={useAltTheme}
                                    isSubList={true}
                                />
                            </div>
                        );
                    }

                    if (showSymbols) {
                        symbolComponent = (
                            <div className={styles.itemSymbol}>
                                <Icon
                                    type={_getSymbolType(isExpanded)}
                                    color={_getSymbolColor(isDisabled)}
                                />
                            </div>
                        );
                    }
                }

                if (secondaryContent) {
                    secondaryContentComponent = (
                        <div className={styles.itemSecondaryContents}>{secondaryContent}</div>
                    );
                }

                const itemTextClassname = classNames(styles.itemText, {
                    [styles.emphazised]: isNavLinkTextEmphasized,
                });

                return (
                    <li
                        className={styles.item}
                        key={path || index}
                        data-testid="eds-nav-list-item"
                        // Set role to "presentation" to intentionally hide semantics from assistive technologies
                        // When the <li> element should not be part of the a11y tree, we want to hide the implied
                        // "listitem" role of the <li> element
                        // role="presentation"
                        onMouseOver={onTentativeSelect}
                        onFocus={onTentativeSelect}
                    >
                        <NavListItem
                            {...unknownItemProps}
                            active={active}
                            isDisabled={isDisabled}
                            isTentativelySelected={isTentativelySelected}
                            onActivate={onLinkClick}
                            onClick={onClick}
                            path={path}
                            tabIndex={tabIndex}
                            type={type}
                            useAltTheme={useAltTheme}
                            useSecondaryTheme={useSecondaryTheme}
                            isSubList={isSubList}
                        >
                            <div className={styles.itemContents}>
                                <div className={itemTextClassname}>{content}</div>
                                {symbolComponent}
                            </div>
                            {secondaryContentComponent}
                        </NavListItem>
                        {nestedListComponent}
                    </li>
                );
            },
        );
    }

    _translateContent(content) {
        const { intl } = this.props;
        if (content && typeof content === 'object') {
            if (content.id && content.defaultMessage) {
                return intl.formatMessage(content);
            }
        }
        return content;
    }

    render() {
        const { items, active, displayInline, useAltTheme, showSymbols, defaultFocusPosition } =
            this.props;
        const className = classNames(styles.root, {
            [styles.inline]: displayInline,
        });

        const translatedItems = items.map((item) => ({
            ...item,
            content: this._translateContent(item.content),
            secondaryContent: this._translateContent(item.secondaryContent),
        }));

        return (
            <HotKeys keyMap={ACTION_KEY_MAP} handlers={this.hotKeyHandlers}>
                <ul data-testid="eds-nav-list" className={className}>
                    {this._getNavListItems(
                        translatedItems,
                        showSymbols,
                        useAltTheme,
                        defaultFocusPosition,
                    )}
                </ul>
            </HotKeys>
        );
    }
}

export const NavList = injectIntl(NavListComponent);
